import {ChangeEvent, useEffect, useRef, useState} from "react";
import styles from './InputWithState.module.scss';
import {useTranslation} from "react-i18next";

interface InputComponentProps {
  type: string;
  handler: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  value: string;
  label: string;
  onSave?: () => void;
  justSaved?: boolean;
}

const InputWithState = ({ type, handler, placeholder, value, label, onSave, justSaved }: InputComponentProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditable, setIsEditable] = useState(false);
  const editStartValueRef = useRef(value);
  const {t, i18n} = useTranslation();

  useEffect(() => {
    if (isEditable) {
      inputRef.current?.focus();
    }
  }, [isEditable]);

  useEffect(() => {
    if (justSaved) {
      setIsEditable(false);
    }
  }, [justSaved]);

  const isDirty = isEditable && value !== editStartValueRef.current;

  const toggleEdit = (next: boolean) => {
    if (next) {
      editStartValueRef.current = value;
    }
    setIsEditable(next);
  };

  const saveAndClose = () => {
    onSave?.();
    toggleEdit(false);
  };

  const dirData = i18n.language === 'ar' ? 'rtl' : 'ltr'

  return (
    <>
      <div className={styles.label}>{label}</div>
      <div className={styles.inputWrapper}>
        {isEditable ? (
          <input
            ref={inputRef}
            onBlur={() => {
              if (!isDirty) toggleEdit(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveAndClose();
              }
            }}
            className={styles.input}
            type={type}
            onChange={handler}
            placeholder={placeholder}
            value={value}
            autoFocus={true}
          />
        ) : (
          <div className={styles.input}>{value || placeholder}</div>
        )}
        <button
          type="button"
          dir={dirData}
          className={styles.disableInputButton}
          onMouseDown={(event) => {
            if (isEditable) event.preventDefault();
          }}
          onClick={() => (isEditable ? saveAndClose() : toggleEdit(true))}
        >
          {isEditable ? t('saveField') : t('change')}
        </button>
      </div>
    </>
  );
};

export default InputWithState;
